import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as categoriesApi from "@api/categories.api";
import type { Category, EntryKind } from "@/types";

interface CategoriesState {
  items: Category[];
  loading: boolean;
}

const initialState: CategoriesState = {
  items: [],
  loading: false,
};

export const fetchCategories = createAsyncThunk(
  "categories/fetchAll",
  async () => {
    return await categoriesApi.getCategories();
  },
);

export const createCategory = createAsyncThunk(
  "categories/create",
  async (data: { name: string; description?: string; kind?: EntryKind }) => {
    return await categoriesApi.createCategory(data);
  },
);

export const updateCategory = createAsyncThunk(
  "categories/update",
  async ({
    id,
    data,
  }: {
    id: string;
    data: { name?: string; description?: string };
  }) => {
    return await categoriesApi.updateCategory(id, data);
  },
);

export const removeCategory = createAsyncThunk(
  "categories/remove",
  async (id: string) => {
    await categoriesApi.deleteCategory(id);
    return id;
  },
);

const categoriesSlice = createSlice({
  name: "categories",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.items = action.payload;
        state.loading = false;
      })
      .addCase(fetchCategories.rejected, (state) => {
        state.loading = false;
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        const idx = state.items.findIndex((c) => c.id === action.payload.id);
        if (idx !== -1) state.items[idx] = action.payload;
      })
      .addCase(removeCategory.fulfilled, (state, action) => {
        state.items = state.items.filter((c) => c.id !== action.payload);
      });
  },
});

export default categoriesSlice.reducer;
